import { getSessionToken } from 'API_LAYER/sessionToken'
import _ from 'lodash'
import { getFriendlyName } from 'Utils/FriendlyName'
import { getCurrentOrg, getUserInfo } from 'Utils/Helpers'
import { axios } from './axios'
import {
  DEFAULT_NAMESPACE_ONLY_ENDPOINT_MAP,
  FETCH_ALL_WHEN_ADMIN_ENDPOINTS,
  PUBLIC_ENDPOINTS
} from './constants'
import { PROCYON_SESSION_HEADER_NAME } from 'Utils/Constants'

/**
 *  @typedef {{ noNamespaceFilter?: boolean }} GetAllApiOptions
 */

/**
 *
 * @param {import('types').Endpoints} endpoint
 * @returns
 */
export function apiClient(endpoint) {
  const user = getUserInfo()
  const { tenant, apiKey } = user
  const org = getCurrentOrg()
  // Get api prefix
  const apiPrefix = PUBLIC_ENDPOINTS.includes(endpoint) ? 'pubapi' : 'api'
  if (!endpoint) throw new Error('API Endpoint is null!')
  const config = {
    baseURL: `/${apiPrefix}/${tenant}/v1/${endpoint}`,
    Namespace: DEFAULT_NAMESPACE_ONLY_ENDPOINT_MAP[endpoint] ? 'default' : org,
    tenant
  }

  /**
   * @type {import('axios').AxiosRequestConfig}
   */
  const axiosConfig = {
    headers: {
      Authorization: `Bearer ${apiKey}`,
      [PROCYON_SESSION_HEADER_NAME]: ''
    },
    params: {
      'ObjectMeta.Namespace': config.Namespace
    }
  }

  return {
    /**
     * @param {import('axios').AxiosRequestConfig['params']} prms
     * @returns
     */
    async getByParams(prms) {
      let url = ''
      try {
        // Merge the params
        _.merge(axiosConfig.params, prms)
        const paramURL = new URLSearchParams(axiosConfig.params).toString()
        url = `${config.baseURL}${paramURL ? '?' + paramURL : ''}`
        // If fetch is running return
        if (FETCHING_KEYS_MAP[url]) return
        FETCHING_KEYS_MAP[url] = true
        const response = await axios.get(url, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          },
          params: {} // already done in the url
        })
        return Object.values(response.data)[0] || null
      } catch (error) {
        console.error('[API CLIENT ERROR]()', error)
        return null
      } finally {
        if (url) {
          FETCHING_KEYS_MAP[url] = false
        }
      }
    },
    /**
     * @param {string} ID
     * @returns
     */
    async getByID(ID) {
      try {
        const params = {
          'ObjectMeta.ID': ID
        }
        const response = await this.getByParams(params)
        return response?.[0]
      } catch (error) {
        console.error('[API CLIENT ERROR]()', error)
        return null
      }
    },
    /**
     *
     * @param {string} name The Object Name to get
     */
    async getByName(name) {
      try {
        const r = await axios.get(`${config.baseURL}/${org}/${name}`, {
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          }
        })
        return r.data || null
      } catch (error) {}
    },
    /**
     * @param {GetAllApiOptions} options
     * @returns
     */
    async getAll(options) {
      const { noNamespaceFilter = false } = options || {}
      let url = ''
      try {
        /**
         ** Some API Endpoints (FETCH_ALL_WHEN_ADMIN_ENDPOINTS), the Namespace param ?ObjectMeta.Namespace=${config.Namespace}
         ** needs to be removed so that admin can see all the objects when in DEFAULT namespace.
         *
         */
        const isAdmin = getUserInfo()?.Spec?.UserRoles?.includes('procyon:admin')
        const isAdminRoute = FETCH_ALL_WHEN_ADMIN_ENDPOINTS[endpoint]
        // User must be admin and the endpoint is supported and, org MUST be DEFAULT
        const namespaceParam =
          noNamespaceFilter || (isAdmin && isAdminRoute && org === 'default')
            ? ''
            : `?ObjectMeta.Namespace=${config.Namespace}`

        url = `${config.baseURL}${namespaceParam}`
        // If fetch is running return
        if (FETCHING_KEYS_MAP[url]) return
        FETCHING_KEYS_MAP[url] = true
        const r = await axios.get(url, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          },
          params: {} // already done in the url
        })
        return Object.values(r.data)[0] || []
      } catch (error) {
        console.error('[API CLIENT ERROR]()', error)
        return []
      } finally {
        if (url) {
          FETCHING_KEYS_MAP[url] = false
        }
      }
    },
    async create(object) {
      try {
        object.ObjectMeta.Name = object.ObjectMeta.Name || getFriendlyName()
        object.ObjectMeta.Namespace = object.ObjectMeta.Namespace || config.Namespace
        object.ObjectMeta.Tenant = config.tenant
        const response = await axios.post(config.baseURL, object, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          },
          params: {} // already done in the url
        })
        return response.data
      } catch (error) {
        console.error('[API CLIENT ERROR]()', error)
        throw error
      }
    },
    async update(object) {
      try {
        const objectBaseURL = createObjectBaseURL(object, { endpoint, tenant })
        const response = await axios.put(objectBaseURL, object, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          },
          params: {} // already done in the url
        })
        return response.data
      } catch (error) {
        console.error('[API CLIENT ERROR]()', error)
        return null
      }
    },
    async delete(object) {
      try {
        const objectBaseURL = createObjectBaseURL(object, { endpoint, tenant })
        await axios.delete(objectBaseURL, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          },
          params: {} // already done in the url
        })
        /**
         * * Backend response for delete doesn't contain info on which object got deleted.
         * * Hence, we return the `object` as is.
         *  */
        return object
      } catch (error) {
        console.error('[API CLIENT ERROR]()', error)
        return null
      }
    },
    async getAllByOffset(startIndex, endIndex) {
      try {
        const url = `${config.baseURL}?ObjectMeta.Namespace=${config.Namespace}`
        const response = await axios.get(url, {
          params: {
            'ObjectMeta.QueryParam': `offset:${startIndex}+limit:${endIndex}`
          },
          paramsSerializer: function (params) {
            var result = Object.keys(params)
              .map(function (key) {
                return key + '=' + params[key]
              })
              .join('&')
            // Build the query string
            return result
          }
        })
        return Object.values(response.data)[0] || null
      } catch (error) {}
    }
  }
}

/**
 * Map which stores GET url endpoints which is running, to prevent duplicate api calls
 */
const FETCHING_KEYS_MAP = {}

const createObjectBaseURL = (object, { endpoint, tenant }) => {
  const name = object.ObjectMeta.Name
  const org = object.ObjectMeta.Namespace
  const apiPrefix = PUBLIC_ENDPOINTS.includes(endpoint) ? 'pubapi' : 'api'

  return `/${apiPrefix}/${tenant}/v1/${endpoint}/${org}/${name}`
}
